import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Briefcase,
  Building2,
  Car,
  Grid2x2,
  MessageCircle,
  Percent,
  Search,
  ShieldCheck,
  Shirt,
  Smartphone,
  Sun,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { Banner, ProductCard as ProductCardData } from "@/api/types";
import { ProductCard, ProductCardSkeleton } from "@/components/commerce/ProductCard";
import { ButtonLink, Sheet } from "@/components/ui";
import { useInfiniteProducts } from "@/features/products/api";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/cn";
import { useReducedMotion } from "@/motion/useReducedMotion";
import { useUI } from "@/stores/ui";
import { HomeHero } from "./HomeHero";

export const PURPOSE = "Buy & sell anything in Nigeria, safely.";
export const PURPOSE_SUB = "Phones, cars, homes, jobs, solar & more. Your money is held until you get your item.";

/** Examples that rotate in the search placeholder: what people really look for here. */
export const SEARCH_EXAMPLES = [
  "Search iPhone 13 in Lagos…",
  "Toyota Corolla 2016…",
  "2 bedroom flat Yaba…",
  "3.5kVA solar kit…",
  "Frontend developer jobs…",
];

export interface HomeCategory {
  label: string;
  slug: string;
  icon: LucideIcon;
}

/** The eight tiles on phones (the last opens every category). */
export const HOME_CATEGORIES: HomeCategory[] = [
  { label: "Phones", slug: "phones-tablets", icon: Smartphone },
  { label: "Fashion", slug: "fashion", icon: Shirt },
  { label: "Power & Solar", slug: "power-energy", icon: Sun },
  { label: "Cars", slug: "vehicles", icon: Car },
  { label: "Property", slug: "property", icon: Building2 },
  { label: "Jobs", slug: "jobs", icon: Briefcase },
  { label: "Services", slug: "services", icon: Wrench },
];

function useRotating<T>(items: T[], ms: number): T {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return undefined;
    const t = window.setInterval(() => setI((n) => (n + 1) % items.length), ms);
    return () => window.clearInterval(t);
  }, [items.length, ms, reduced]);
  return items[i]!;
}

/** Big search pill with a rotating example; opens the full search screen (recent + trending). */
export function HomeSearch({ className }: { className?: string }) {
  const setSearchOpen = useUI((s) => s.setSearchOpen);
  const example = useRotating(SEARCH_EXAMPLES, 2500);
  return (
    <div role="search" className={className}>
      <button
        type="button"
        onClick={() => {
          track("home_search_used", { source: "hero" });
          setSearchOpen(true);
        }}
        className={cn(
          "flex h-[52px] w-full items-center gap-3 rounded-full border border-line bg-surface px-4 text-left shadow-card transition hover:border-[var(--border-glow)] active:scale-[0.995]",
        )}
      >
        <Search className="size-5 shrink-0 text-[var(--primary-text)]" aria-hidden />
        <span className="relative h-6 min-w-0 flex-1 overflow-hidden text-[15px] text-ink-muted" aria-hidden>
          <span key={example} className="absolute inset-0 animate-fade-in truncate">
            {example}
          </span>
        </span>
        <span className="sr-only">Search RealityStore</span>
      </button>
    </div>
  );
}

/** 2 × 4 icon tiles: seven popular categories and "More". */
export function CategoryTiles({ className }: { className?: string }) {
  const setCategoriesOpen = useUI((s) => s.setCategoriesOpen);
  const tile = "flex flex-col items-center gap-1.5 rounded-xl py-1.5 text-center active:bg-fg/5";
  const circle =
    "grid size-12 place-items-center rounded-full bg-surface2 text-[var(--primary-text)] transition group-hover:bg-[var(--brand-tint)]";
  const label = "line-clamp-1 text-[12px] font-medium leading-tight text-ink";
  return (
    <nav aria-label="Popular categories" className={className}>
      <ul className="grid grid-cols-4 gap-x-1 gap-y-2">
        {HOME_CATEGORIES.map(({ label: name, slug, icon: Icon }, i) => (
          <li key={slug}>
            <Link to={`/c/${slug}`} className={cn("group", tile)} onClick={() => track("home_category_tap", { slug, position: i + 1 })}>
              <span className={circle}>
                <Icon className="size-[22px]" strokeWidth={1.8} aria-hidden />
              </span>
              <span className={label}>{name}</span>
            </Link>
          </li>
        ))}
        <li>
          <button
            type="button"
            className={cn("group w-full", tile)}
            onClick={() => {
              track("home_category_tap", { slug: "more", position: 8 });
              setCategoriesOpen(true);
            }}
          >
            <span className={circle}>
              <Grid2x2 className="size-[22px]" strokeWidth={1.8} aria-hidden />
            </span>
            <span className={label}>More</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

const SAFE_STEPS = [
  { icon: Wallet, title: "You pay RealityStore", body: "Pay at checkout with RealitySafe. The seller doesn't get the money yet." },
  { icon: ShieldCheck, title: "We hold your money", body: "It stays with us while the seller sends your item." },
  {
    icon: BadgeCheck,
    title: "Seller gets paid when you confirm",
    body: "Got it and it's as described? Confirm, and we release the payment. If not, open a dispute.",
  },
];

/** Bottom sheet: how RealitySafe keeps your money safe, in three steps. */
export function RealitySafeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="How RealitySafe works" description="Your money is held until you get your item.">
      <ol className="flex flex-col gap-4 pb-3">
        {SAFE_STEPS.map(({ icon: Icon, title, body }, i) => (
          <li key={title} className="flex gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--safe-tint)] text-success">
              <Icon className="size-5" aria-hidden />
            </span>
            <span>
              <span className="block font-semibold text-ink">
                {i + 1}. {title}
              </span>
              <span className="mt-0.5 block text-sm text-ink-soft">{body}</span>
            </span>
          </li>
        ))}
        <li>
          <ButtonLink to="/safety" variant="ghost" size="sm" onClick={onClose}>
            Learn more <ArrowRight className="size-4" aria-hidden />
          </ButtonLink>
        </li>
      </ol>
    </Sheet>
  );
}

/** One line of trust chips; "Money held until delivery" explains RealitySafe. */
export function TrustStrip({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const chip = "inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] font-medium";
  return (
    <>
      <ul
        aria-label="Why shop here"
        className={cn("-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&>li]:shrink-0", className)}
      >
        <li>
          <button
            type="button"
            onClick={() => {
              track("realitysafe_sheet_open", { source: "trust_strip" });
              setOpen(true);
            }}
            className={cn(chip, "border-success/30 bg-[var(--safe-tint)] text-success")}
          >
            <ShieldCheck className="size-4" aria-hidden /> Money held until delivery
          </button>
        </li>
        <li className={cn(chip, "border-line bg-surface text-ink-soft")}>
          <BadgeCheck className="size-4 text-[var(--primary-text)]" aria-hidden /> Verified sellers
        </li>
        <li className={cn(chip, "border-line bg-surface text-ink-soft")}>
          <MessageCircle className="size-4 text-[var(--primary-text)]" aria-hidden /> Chat &amp; negotiate
        </li>
        <li className={cn(chip, "border-line bg-surface text-ink-soft")}>
          <Percent className="size-4 text-[var(--primary-text)]" aria-hidden /> 0% commission for sellers
        </li>
      </ul>
      <RealitySafeSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** Popular listings in the shopper's state; falls back to all of Nigeria when the state is quiet. */
export function useTrending(stateId: number | null, size = 12) {
  const local = useInfiniteProducts({ sort: "popular", page_size: size, ...(stateId ? { state: stateId } : {}) });
  const localItems = local.data?.pages[0]?.results ?? [];
  const needFallback = !!stateId && local.isSuccess && localItems.length < 4;
  const national = useInfiniteProducts({ sort: "popular", page_size: size }, needFallback);
  const items: ProductCardData[] = needFallback ? (national.data?.pages[0]?.results ?? []) : localItems;
  const loading = local.isLoading || (needFallback && national.isLoading);
  return { items, loading, local: !needFallback && !!stateId };
}

/** Phones: a sideways row whose top edge shows above the fold ("scroll for more"). */
export function TrendingRow({ items, loading, title }: { items: ProductCardData[]; loading: boolean; title: string }) {
  return (
    <section aria-labelledby="trending-h" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 id="trending-h" className="text-lg font-bold">
          {title}
        </h2>
        <Link to="/search?sort=popular" className="text-sm font-medium text-[var(--primary-text)]">
          See all
        </Link>
      </div>
      <ul className="-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <li key={i} className="w-[44vw] max-w-[190px] shrink-0">
                <ProductCardSkeleton />
              </li>
            ))
          : items.map((p, i) => (
              <li
                key={p.id}
                className="w-[44vw] max-w-[190px] shrink-0 snap-start"
                onClickCapture={() => track("home_listing_tap", { slug: p.slug, position: i + 1, source: "trending" })}
              >
                <ProductCard product={p} priority={i < 2} />
              </li>
            ))}
      </ul>
    </section>
  );
}

/** Desktop and tablet: the trending grid (2 → 3 → 4 → 6 columns). */
export function TrendingGrid({
  items,
  loading,
  title,
  id = "trending-grid-h",
}: {
  items: ProductCardData[];
  loading: boolean;
  title: string;
  id?: string;
}) {
  return (
    <section aria-labelledby={id} className="container-rs flex flex-col gap-4">
      <div className="flex items-end justify-between gap-3">
        <h2 id={id} className="text-xl font-bold sm:text-2xl">
          {title}
        </h2>
        <Link to="/search?sort=popular" className="inline-flex items-center gap-1 text-sm font-medium text-[var(--primary-text)]">
          See all <ArrowRight className="size-4" aria-hidden />
        </Link>
      </div>
      <ul className={PRODUCT_GRID}>
        {loading
          ? Array.from({ length: 12 }, (_, i) => (
              <li key={i}>
                <ProductCardSkeleton />
              </li>
            ))
          : items.map((p, i) => (
              <li key={p.id} onClickCapture={() => track("home_listing_tap", { slug: p.slug, position: i + 1, source: "trending_grid" })}>
                <ProductCard product={p} priority={i < 6} />
              </li>
            ))}
      </ul>
    </section>
  );
}

/** Product grid columns used site-wide: 2 (< 600px), 3 (600–899), 4 (900–1199), 6 (≥ 1200). */
export const PRODUCT_GRID =
  "grid grid-cols-2 gap-3 min-[600px]:grid-cols-3 min-[900px]:grid-cols-4 min-[1200px]:grid-cols-6 min-[1200px]:gap-4";

/** Phones (and the installed app below desktop): everything a first-time visitor needs, above the fold. */
export function MobileAboveFold({ stateName, trending }: { stateName: string | null; trending: ReturnType<typeof useTrending> }) {
  return (
    <div className="flex flex-col gap-4 px-4 pt-3">
      <section aria-labelledby="home-h1" className="-mx-4 -mt-3 bg-[linear-gradient(180deg,var(--brand-tint),transparent)] px-4 pb-1 pt-4">
        <h1 id="home-h1" className="text-[26px] font-bold leading-[1.15] tracking-tight text-ink">
          {PURPOSE}
        </h1>
        <p className="mt-1.5 text-[15px] leading-snug text-ink-soft">{PURPOSE_SUB}</p>
      </section>
      <HomeSearch />
      <CategoryTiles />
      <TrustStrip />
      <TrendingRow
        items={trending.items}
        loading={trending.loading}
        title={trending.local && stateName ? `Trending in ${stateName}` : "Trending in Nigeria"}
      />
    </div>
  );
}

const DESKTOP_TRUST = [
  { icon: ShieldCheck, text: "Your money is held until you get your item" },
  { icon: BadgeCheck, text: "Verified sellers with ID and bank checks" },
  { icon: MessageCircle, text: "Chat, negotiate and agree a price" },
];

/** Desktop: purpose + trust + actions with a featured carousel (2/3), and the RealitySafe card (1/3). */
export function DesktopHero({ banners, stateId, featured }: { banners: Banner[]; stateId: number | null; featured: ProductCardData[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="home-h1" className="container-rs">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
        <div className="grid h-[320px] grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] overflow-hidden rounded-[16px] border border-line bg-surface shadow-card">
          <div className="flex flex-col justify-center gap-2.5 bg-[linear-gradient(135deg,var(--brand-tint),transparent_70%)] px-7 py-6">
            <h1 id="home-h1" className="text-[28px] font-bold leading-[1.1] tracking-tight text-ink xl:text-[32px]">
              {PURPOSE}
            </h1>
            <p className="text-[15px] leading-snug text-ink-soft">{PURPOSE_SUB}</p>
            <ul className="flex flex-col gap-1">
              {DESKTOP_TRUST.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-2 text-sm text-ink-soft">
                  <Icon className="size-4 shrink-0 text-success" aria-hidden /> {text}
                </li>
              ))}
            </ul>
            <div className="mt-1 flex gap-2">
              <ButtonLink to="#trending" size="md" onClick={() => track("home_search_used", { source: "start_shopping" })}>
                Start shopping
              </ButtonLink>
              <ButtonLink to="/sell" variant="ghost" size="md" onClick={() => track("home_sell_tap", { source: "hero" })}>
                Sell for free
              </ButtonLink>
            </div>
          </div>
          <div className="relative min-w-0 p-3 pl-0">
            {banners.length ? <HomeHero banners={banners} stateId={stateId} fill /> : <FeaturedMosaic items={featured} />}
          </div>
        </div>
        <aside
          aria-labelledby="safe-h"
          className="flex h-[300px] flex-col rounded-[16px] border border-success/25 bg-surface p-6 shadow-card"
        >
          <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--safe-tint)] px-2.5 py-1 text-xs font-semibold text-success">
            <ShieldCheck className="size-3.5" aria-hidden /> RealitySafe
          </p>
          <h2 id="safe-h" className="mt-3 text-xl font-bold">
            How your money stays safe
          </h2>
          <ol className="mt-3 flex flex-1 flex-col gap-2.5">
            {["Pay", "We hold it", "Seller gets paid when you confirm"].map((step, i) => (
              <li key={step} className="flex items-center gap-3 text-[15px] text-ink">
                <span className="tabular grid size-7 shrink-0 place-items-center rounded-full bg-[var(--safe-tint)] text-sm font-bold text-success">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={() => {
              track("realitysafe_sheet_open", { source: "desktop_card" });
              setOpen(true);
            }}
            className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-[var(--primary-text)] hover:underline"
          >
            Learn more <ArrowRight className="size-4" aria-hidden />
          </button>
        </aside>
      </div>
      <RealitySafeSheet open={open} onClose={() => setOpen(false)} />
    </section>
  );
}

/** When no hero banners or ads are booked: four real listings instead of an empty panel. */
function FeaturedMosaic({ items }: { items: ProductCardData[] }) {
  const four = items.slice(0, 4);
  if (!four.length) return <div className="h-full rounded-[12px] bg-surface2" aria-hidden />;
  return (
    <ul className="grid h-full grid-cols-2 grid-rows-2 gap-2" aria-label="Featured listings">
      {four.map((p) => (
        <li key={p.id} className="min-h-0">
          <Link
            to={`/p/${p.slug}`}
            onClick={() => track("home_listing_tap", { slug: p.slug, source: "hero_mosaic" })}
            className="group relative block h-full overflow-hidden rounded-[12px] border border-line bg-surface2"
          >
            {p.cover?.url && (
              <img src={p.cover.url} alt="" loading="lazy" className="size-full object-cover transition group-hover:scale-[1.03]" />
            )}
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-scrim/80 to-transparent p-2 pt-6">
              <span className="tabular block text-sm font-bold text-white">₦{Math.round(p.price / 100).toLocaleString("en-NG")}</span>
              <span className="block truncate text-xs text-white/85">{p.title}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
